import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { Lock, ShieldCheck, Loader2, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlatformGate — step-up authentication for /platform/* routes.
 * Renders children only when the backend confirms the platform owner has
 * unlocked their session within the last 30 minutes. Otherwise shows a
 * password challenge.
 */
export default function PlatformGate({ children }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | locked | unlocked | bypass
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);

  const token = () => localStorage.getItem('session_token');
  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const refreshStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/unlock-status`, {
        headers: authHeaders(), credentials: 'include',
      });
      if (r.status === 401) { navigate('/login'); return; }
      const d = await r.json();
      // Non-owner roles (super_agent / agent_admin viewing /platform/buildings) bypass the gate;
      // ProtectedRoute already enforces their allowed access. PlatformGate is only a step-up
      // challenge for platform_owners.
      if (d.role !== 'platform_owner') { setStatus('bypass'); return; }
      if (d.unlocked) { setStatus('unlocked'); setExpiresAt(d.expires_at); return; }
      setStatus('locked');
    } catch (e) {
      toast.error('Could not verify unlock status');
      setStatus('locked');
    }
  };

  useEffect(() => { refreshStatus(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password) return toast.error('Password required');
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/unlock`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Unlock failed');
      toast.success(`Unlocked for ${d.minutes} minutes`);
      setPassword('');
      setExpiresAt(d.expires_at);
      setStatus('unlocked');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const lockNow = async () => {
    try {
      await fetch(`${API_URL}/api/platform-admin/lock`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      toast.success('Locked');
      setStatus('locked');
    } catch {/* noop */}
  };

  if (status === 'checking') {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }
  if (status === 'bypass') {
    return <>{children}</>;
  }
  if (status === 'locked') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-2xl" data-testid="platform-gate-card">
          <CardHeader>
            <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center mb-3">
              <Lock className="w-6 h-6" />
            </div>
            <CardTitle className="text-2xl">Super Admin verification</CardTitle>
            <CardDescription>Enter the platform admin password to continue. Session unlocks for 30 minutes.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <Input
                type="password"
                placeholder="Platform admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="platform-gate-password"
              />
              <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="platform-gate-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Unlock
              </Button>
              <p className="text-xs text-slate-400 text-center">Failed attempts are audit-logged.</p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <>
      {children}
      <div className="fixed bottom-3 right-3 z-50">
        <button
          onClick={lockNow}
          title={`Unlocked until ${expiresAt ? new Date(expiresAt).toLocaleTimeString() : ''}`}
          className="text-xs bg-slate-900/80 text-white px-3 py-1.5 rounded-full hover:bg-slate-900 backdrop-blur shadow-lg flex items-center gap-1"
          data-testid="platform-gate-lock-btn"
        >
          <LogOut className="w-3 h-3" /> Lock super admin
        </button>
      </div>
    </>
  );
}
